import { useEffect, useRef } from "react";
import Button from "../Button";
import {
  type PluralPartyField,
  usePluralPartyToggle,
} from "./usePluralPartyToggle";

interface PluralPartyToggleProps {
  field: PluralPartyField;
  documentId?: string;
  caseId?: string;
  /** Called with the new value after each toggle, in both API and local-state modes. */
  onChange?: (value: boolean) => void;
  /** Used only when neither documentId nor caseId is provided (local-state mode). */
  value?: boolean;
}

export default function PluralPartyToggle(props: PluralPartyToggleProps) {
  const hasApiMode = !!props.documentId || !!props.caseId;
  const toggle = usePluralPartyToggle(props.field, props.documentId, props.caseId);

  const currentValue = hasApiMode ? toggle.value : (props.value ?? false);

  // Sync the parent's label state once when the query resolves. Without this,
  // the parent's local state (used for label text) starts from a stale default
  // while the toggle's value comes from the query, causing a mismatch.
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;
  const hasInitiallySynced = useRef(false);

  useEffect(() => {
    if (!hasApiMode || toggle.isLoading || hasInitiallySynced.current) return;
    hasInitiallySynced.current = true;
    onChangeRef.current?.(toggle.value);
  }, [hasApiMode, toggle.isLoading, toggle.value]);

  function handleClick() {
    if (hasApiMode) {
      const newValue = toggle.onToggle();
      props.onChange?.(newValue);
    } else {
      const newValue = !(props.value ?? false);
      props.onChange?.(newValue);
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleClick}
      disabled={hasApiMode && toggle.isPending}
    >
      {currentValue ? "Singularize" : "Pluralize"}
    </Button>
  );
}
